import { useCallback, useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Divider, ListItemIcon, ListItemText, FormHelperText, Box,
} from '@mui/material'
import { CreateNewFolder, AutoAwesome } from '@mui/icons-material'
import { api } from '../../services/api'
import { foldersNewestFirst } from '../../utils/folderSelection'
import { basePlanLabel } from '../../utils/basePlanLabel'
import BasePlanWarnings from '../planningboard/BasePlanWarningsLink'

// FAR-50: New Production Plan.
//   Name, Description, Folder (defaults to the selected folder).
//   Base Plan -- mandatory. The plant's Base Plans, newest first, plus
//     "Generate a new Base Plan", which builds one from the latest uploaded
//     data (POST /base-plans, a few seconds) and selects it.
//   Modification List -- optional. The plant's lists, newest first. "None"
//     starts the plan with no modifications; a new list is saved with the
//     planner's first modification (FAR-56), because lists are never empty and
//     never edited once saved (FAR-52; Mark on FAR-50).
// Per FAR-59, both pickers show only the plant being planned.

const GENERATE = '__generate__'
const NEW_FOLDER = '__new__'

const blankForm = (folderId) => ({
  name: '', description: '', folderId: folderId || '', basePlanId: '', modificationListId: '',
})

export default function CreatePlanModal({
  open, onClose, onSubmit, folders, onCreateFolder, loading, error, plant, defaultFolderId,
}) {
  const [form, setForm] = useState(blankForm(defaultFolderId))
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')

  const [basePlans, setBasePlans] = useState([])
  const [modLists, setModLists] = useState([])
  const [pickersError, setPickersError] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState(null)

  const plantCode = plant?.plantCode

  const loadPickers = useCallback(async () => {
    if (!plantCode) return
    setPickersError(null)
    try {
      const [bps, lists] = await Promise.all([
        api.get(`/base-plans?plant=${encodeURIComponent(plantCode)}`),
        api.get(`/modification-lists?plant=${encodeURIComponent(plantCode)}`),
      ])
      const newestFirst = (a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')
      const bpList = Array.isArray(bps) ? [...bps].sort(newestFirst) : []
      setBasePlans(bpList)
      setModLists(Array.isArray(lists) ? [...lists].sort(newestFirst) : [])
      // Default to the newest Base Plan -- usually today's daily run.
      setForm((prev) => (prev.basePlanId || !bpList.length ? prev : { ...prev, basePlanId: bpList[0].id }))
    } catch (err) {
      setPickersError(err.message || 'Could not load Base Plans and Modification Lists')
    }
  }, [plantCode])

  // Fresh form each time the modal opens; the folder defaults to the one
  // the planner is looking at.
  useEffect(() => {
    if (!open) return
    setForm(blankForm(defaultFolderId))
    setGenerateError(null)
    loadPickers()
  }, [open, defaultFolderId, loadPickers])

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))
  const valid = form.name.trim().length > 0 && !!plantCode && !!form.basePlanId && !generating

  const generateBasePlan = async () => {
    setGenerating(true)
    setGenerateError(null)
    try {
      const bp = await api.post('/base-plans', { plant: plantCode })
      setBasePlans((prev) => [bp, ...prev.filter((p) => p.id !== bp.id)])
      setForm((prev) => ({ ...prev, basePlanId: bp.id }))
    } catch (err) {
      setGenerateError(err.message || 'Could not generate a Base Plan')
    } finally {
      setGenerating(false)
    }
  }

  const handleBasePlanChange = (e) => {
    if (e.target.value === GENERATE) generateBasePlan()
    else set('basePlanId')(e)
  }

  const handleSubmit = () => {
    if (!valid) return
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim(),
      plant: plantCode,
      folderId: form.folderId || undefined,
      basePlanId: form.basePlanId,
      modificationListId: form.modificationListId || undefined,
    })
  }

  const handleFolderChange = (e) => {
    if (e.target.value === NEW_FOLDER) {
      setNewFolderName(new Date().toISOString().slice(0, 10))
      setCreatingFolder(true)
    } else {
      set('folderId')(e)
    }
  }

  const confirmNewFolder = () => {
    const name = newFolderName.trim()
    // FAR-49: the handler takes the same { name, description } the Create a
    // Folder form submits; a folder made here has no description.
    if (name && onCreateFolder) onCreateFolder({ name, description: '' })
    setCreatingFolder(false)
    setNewFolderName('')
  }

  const handleClose = () => {
    setCreatingFolder(false)
    onClose()
  }

  const selectedBasePlan = basePlans.find((b) => b.id === form.basePlanId)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create a Production Plan</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {pickersError && <Alert severity="error">{pickersError}</Alert>}

        <TextField
          label="Production Plan Name"
          required
          value={form.name}
          onChange={set('name')}
          inputProps={{ maxLength: 120 }}
          helperText={`${form.name.length}/120`}
          autoFocus
        />

        <TextField
          label="Description"
          value={form.description}
          onChange={set('description')}
          multiline
          rows={3}
        />

        <TextField label="Plant" value={plant ? `${plant.name} (${plant.plantCode})` : ''} disabled helperText="Plans are created for the plant you're working in" />

        {creatingFolder ? (
          <TextField
            autoFocus
            label="New Folder Name"
            size="small"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') confirmNewFolder()
              if (e.key === 'Escape') setCreatingFolder(false)
            }}
            onBlur={confirmNewFolder}
          />
        ) : (
          <FormControl>
            <InputLabel>Folder</InputLabel>
            <Select value={form.folderId} label="Folder" onChange={handleFolderChange}>
              <MenuItem value="">(None)</MenuItem>
              {foldersNewestFirst(folders).map((f) => (
                <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
              ))}
              <Divider />
              <MenuItem value={NEW_FOLDER}>
                <ListItemIcon><CreateNewFolder fontSize="small" /></ListItemIcon>
                <ListItemText>New Folder</ListItemText>
              </MenuItem>
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth required error={!!generateError}>
          <InputLabel>Base Plan</InputLabel>
          <Select
            value={form.basePlanId}
            label="Base Plan"
            onChange={handleBasePlanChange}
            disabled={generating}
            data-testid="base-plan-picker"
          >
            {basePlans.length === 0 && (
              // FAR-70: an empty list says so.
              <MenuItem disabled value="__none__" data-testid="no-base-plans"><em>No Plans Exist.</em></MenuItem>
            )}
            {basePlans.map((bp) => (
              <MenuItem key={bp.id} value={bp.id}>{basePlanLabel(bp)}</MenuItem>
            ))}
            {basePlans.length > 0 && <Divider />}
            <MenuItem value={GENERATE}>
              <ListItemIcon><AutoAwesome fontSize="small" /></ListItemIcon>
              <ListItemText
                primary="Generate a new Base Plan"
                secondary="From the latest uploaded forecast and inventory"
              />
            </MenuItem>
          </Select>
          <FormHelperText>
            {generating ? (
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={12} /> Generating a Base Plan from the latest data…
              </Box>
            ) : generateError
              ? generateError
              : selectedBasePlan
                ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {`${(selectedBasePlan.rowCount || 0).toLocaleString()} items, ${Math.round(selectedBasePlan.totalScheduledCases || 0).toLocaleString()} cases scheduled`}
                    {/* FAR-97: the Base Plan's stored warnings, if any. */}
                    <BasePlanWarnings basePlan={selectedBasePlan} title={`${basePlanLabel(selectedBasePlan)} warnings`} />
                  </Box>
                )
                : basePlans.length === 0
                  ? 'No Base Plans yet. Generate one from the latest uploaded data.'
                  : 'Required'}
          </FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Modification List</InputLabel>
          <Select
            value={form.modificationListId}
            label="Modification List"
            onChange={set('modificationListId')}
            data-testid="modification-list-picker"
          >
            <MenuItem value=""><em>None (start with no modifications)</em></MenuItem>
            {modLists.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name} ({l.ruleCount} {l.ruleCount === 1 ? 'rule' : 'rules'})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Optional. Changes to an existing list are saved as a new list; the original is never edited.
          </FormHelperText>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!valid || loading}
          startIcon={loading ? <CircularProgress size={18} /> : undefined}
        >
          Create Plan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
